import { formatDate } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { RootState } from '@root/app.state';
import { selectUserOverview } from '@core/core.selectors';
import * as RouterActions from '@platform/router.actions';
import { go } from '@platform/router.actions';
import { UserOverview } from '@core/core.state';
import { UserOverviewDto } from '@core/users/user.model';
import { defaultPageSize, getPageSizeOptions } from '@platform/shared/utilities/paginator-utility';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false,
})
export class UserListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  userDataSource = new MatTableDataSource<UserOverview>([]);
  searchForm!: UntypedFormGroup;
  randomName1 = Math.random().toString();
  defaultPageSize = defaultPageSize;
  pageSizeOptions = getPageSizeOptions;

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private store$: Store<RootState>,
    private formBuilder: UntypedFormBuilder,
    private destroyRef: DestroyRef,
  ) {
    this.store$
      .pipe(
        select(selectUserOverview),
        map((state) => state.data),
        filter((data) => !!data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.userDataSource.data = value;
      });
  }

  public get columns(): string[] {
    return ['firstName', 'lastName', 'account', 'organization', 'email', 'createdDate', 'lastLogin', 'isActive', 'edit'];
  }

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({
      search: [''],
    });
    this.setFilteringOfDataSource();
    this.subscribeToSearchChanges();

    this.userDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'firstName': {
          return item.firstName.toLocaleLowerCase();
        }
        case 'lastName': {
          return item.lastName.toLocaleLowerCase();
        }
        case 'lastLogin': {
          return item.lastSuccessfulLoginDate?.getTime().toString();
        }
        case 'createdDate': {
          return item.accountCreatedDate?.getTime().toString();
        }
        case 'account': {
          return item.isInternal;
        }
        case 'organization': {
          return item.organizationUnit?.toLocaleLowerCase();
        }
        case 'email': {
          return item.mail?.toLocaleLowerCase();
        }
        default: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          return item[property];
        }
      }
    };
  }

  public navigateUsersCreate() {
    this.store$.dispatch(RouterActions.go({ path: ['/', 'users', 'create'] }));
  }

  getAccountType(isInternal: boolean): string {
    return isInternal ? 'Internal' : 'External';
  }

  getIsActiveValueTranslationKey(isActive: boolean): string {
    return isActive ? 'shared.yes' : 'shared.no';
  }

  ngAfterViewInit() {
    this.userDataSource.sort = this.sort;
    this.userDataSource.paginator = this.paginator;
  }

  navigateToUserProfile(user: UserOverviewDto, $event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.store$.dispatch(
      go({
        path: ['/', 'users', user.id],
      }),
    );
  }

  private subscribeToSearchChanges() {
    this.searchForm.get('search')!.valueChanges.subscribe((value) => {
      this.userDataSource.filter = value;
    });
  }

  private setFilteringOfDataSource() {
    this.userDataSource.filterPredicate = (data, searchText: string) => {
      if (!searchText) {
        return true;
      }
      const concatObject = `${data.firstName} ${data.lastName} ${data.mail} ${data.organizationUnit} ${this.getAccountType(
        data.isInternal,
      )} ${formatDate(data.accountCreatedDate, 'shortDate', this.locale)} ${formatDate(
        data.lastSuccessfulLoginDate,
        'shortDate',
        this.locale,
      )}`;
      return concatObject.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    };
  }
}
